import React from 'react';
import { AppSelect } from '../../../components/AppSelect';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { ClampedTextWithTooltip } from '../../../components/ClampedTextWithTooltip';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { CardListSkeleton, TableSkeletonRows } from '../../../components/TableSkeleton';
import { OFFER_UNIT_OPTIONS } from '../../../constants/offerUnits';
import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { ArrowLeft, Pencil, Search, Trash2, X } from 'lucide-react';
import type { Leistung, LeistungFolder } from '../types/leistung';
import { LeistungCreateModal } from '../components/LeistungCreateModal';
import {
  createLeistung,
  deleteLeistung,
  loadLeistungenPage,
  updateLeistung,
} from '../utils/leistungApi';
import { MaterialSearchPagination } from '../components/materialien/MaterialSearchPagination';

export type { Leistung } from '../types/leistung';

function calcVk(kosten: string, surchargePct: string): number {
  const k = parseGermanNumber(kosten);
  const sur = parseGermanNumber(surchargePct);
  return k * (1 + sur / 100);
}

function leistungToEditDraft(l: Leistung): Partial<Leistung> {
  const sur = typeof l.surcharge_percent === 'number' ? l.surcharge_percent : 0;
  return { ...l, surcharge_percent: sur };
}

export type LeistungenSectionProps = {
  selectedFolderId: string | null;
  leistungFolders: LeistungFolder[];
  leistungOrdnerIds: Set<string>;
  onFoldersRefresh: () => void | Promise<void>;
  onTotalCountChange: (count: number) => void;
  mobileCreateOpen?: boolean;
  onCloseMobileCreate?: () => void;
};

export const LeistungenSection: React.FC<LeistungenSectionProps> = ({
  selectedFolderId,
  leistungFolders,
  leistungOrdnerIds,
  onFoldersRefresh,
  onTotalCountChange,
  mobileCreateOpen = false,
  onCloseMobileCreate,
}) => {
  const [leistungen, setLeistungen] = React.useState<Leistung[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resultsCount, setResultsCount] = React.useState(0);
  const [listPage, setListPage] = React.useState(1);
  const [listTotalPages, setListTotalPages] = React.useState(1);
  // Die Tabelle kann breiter als der Viewport werden — Shift+Rad ersetzt das Ziehen der Leiste.
  const setTableScrollNode = useHorizontalWheelScroll<HTMLDivElement>();
  const listSectionRef = React.useRef<HTMLElement | null>(null);
  const loadAbortRef = React.useRef<AbortController | null>(null);
  const prevFolderRef = React.useRef<string | null | undefined>(undefined);

  const [lArticleNumber, setLArticleNumber] = React.useState('');
  const [lName, setLName] = React.useState('');
  const [lDescription, setLDescription] = React.useState('');
  const [lUnit, setLUnit] = React.useState('');
  const [lKosten, setLKosten] = React.useState('');
  const [lSurcharge, setLSurcharge] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchGeneration, setSearchGeneration] = React.useState(0);
  const [searchSubmitting, setSearchSubmitting] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<Partial<Leistung>>({});
  const [mobileEditLeistung, setMobileEditLeistung] = React.useState<Leistung | null>(null);

  const loadLeistungenList = React.useCallback(
    (
      folderId: string | null = selectedFolderId,
      query: string = searchQuery,
      page = listPage,
    ) => {
      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      setLoading(true);
      return loadLeistungenPage(folderId, query, page, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setLeistungen(result.rows);
          setResultsCount(result.total);
          setListTotalPages(result.totalPages);
          setListPage(result.page);
          if (result.globalTotal != null) onTotalCountChange(result.globalTotal);
        })
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setLeistungen([]);
          setResultsCount(0);
          setListTotalPages(1);
        })
        .finally(() => {
          if (loadAbortRef.current !== controller) return;
          loadAbortRef.current = null;
          setLoading(false);
          setSearchSubmitting(false);
        });
    },
    [selectedFolderId, searchQuery, listPage, onTotalCountChange],
  );

  const triggerLeistungSearch = React.useCallback(() => {
    const trimmed = searchInput.trim();
    setSearchSubmitting(Boolean(trimmed));
    setListPage(1);
    setSearchQuery(trimmed);
    setSearchGeneration((g) => g + 1);
  }, [searchInput]);

  React.useEffect(() => () => loadAbortRef.current?.abort(), []);

  // Search as you type (no button); an emptied field goes back to the full list right away.
  React.useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery) return;
    const t = window.setTimeout(triggerLeistungSearch, trimmed ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [searchInput, searchQuery, triggerLeistungSearch]);

  React.useEffect(() => {
    const folderChanged =
      prevFolderRef.current !== undefined && prevFolderRef.current !== selectedFolderId;
    prevFolderRef.current = selectedFolderId;
    const query = folderChanged ? '' : searchQuery;
    const page = folderChanged ? 1 : listPage;
    if (folderChanged && searchQuery) {
      setSearchQuery('');
      setSearchInput('');
    }
    void loadLeistungenList(selectedFolderId, query, page);
  }, [loadLeistungenList, selectedFolderId, searchQuery, listPage, searchGeneration]);

  const refreshAfterMutation = React.useCallback(() => {
    void loadLeistungenList();
    void onFoldersRefresh();
  }, [loadLeistungenList, onFoldersRefresh]);

  const onError = React.useCallback((msg: string | null) => setError(msg), []);

  const previewVk = React.useMemo(() => calcVk(lKosten, lSurcharge), [lKosten, lSurcharge]);
  const folderOptions = React.useMemo(
    () => [
      { value: '', label: 'Kein Ordner' },
      ...leistungFolders.map((folder) => ({ value: folder.id, label: folder.name })),
    ],
    [leistungFolders]
  );
  const folderNameById = React.useMemo(
    () => new Map(leistungFolders.map((folder) => [folder.id, folder.name])),
    [leistungFolders]
  );

  const formatPrice = (value: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '-';
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${formatted}€`;
  };

  const validateSurcharge = (v: number) => Number.isFinite(v) && v >= 0;

  const closeCreate = React.useCallback(() => {
    setCreateOpen(false);
    onCloseMobileCreate?.();
  }, [onCloseMobileCreate]);

  const handleCreate = (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const fail = (message: string) => {
      onError(message);
      return Promise.reject(new Error(message));
    };
    if (!lName.trim()) return fail('Name ist erforderlich.');
    if (!lUnit.trim()) return fail('Einheit ist erforderlich.');
    if (!lKosten.trim()) return fail('Kosten sind erforderlich.');
    const sur = parseGermanNumber(lSurcharge);
    if (!validateSurcharge(sur)) {
      return fail('Der Zuschlag darf nicht negativ sein.');
    }
    setSaving(true);
    onError(null);
    const folderPayload =
      selectedFolderId && leistungOrdnerIds.has(selectedFolderId) ? { folder_id: selectedFolderId } : {};
    return createLeistung({
      article_number: lArticleNumber.trim() || null,
      name: lName.trim(),
      description: lDescription.trim() || null,
      unit: lUnit || null,
      net_cost: parseGermanNumber(lKosten),
      surcharge_percent: sur,
      ...folderPayload,
    })
      .then(() => {
        refreshAfterMutation();
        setLArticleNumber('');
        setLName('');
        setLDescription('');
        setLUnit('');
        setLKosten('');
        setLSurcharge('');
        closeCreate();
      })
      .catch((err) => {
        onError(err.message || 'Leistung konnte nicht angelegt werden.');
        throw err;
      })
      .finally(() => setSaving(false));
  };

  const emptyMessage = searchQuery.trim()
    ? `Keine Treffer für „${searchQuery.trim()}“.`
    : selectedFolderId
      ? 'Keine Leistungen in dieser Ansicht (Ordner anpassen).'
      : 'Noch keine Leistungen erfasst.';

  return (
    <>
      {error && !createOpen && !mobileCreateOpen && (
        <div className="m-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex justify-between items-center">
          <span>{error}</span>
          <button type="button" onClick={() => onError(null)} className="text-red-500 hover:underline">
            Schließen
          </button>
        </div>
      )}

      <section
        ref={listSectionRef}
        className="-mx-3 -mt-3 md:mx-0 md:mt-0 bg-app-surface border-0 overflow-hidden md:flex md:min-h-0 md:flex-1 md:flex-col"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-app-border px-4 py-3">
          <div className="hidden min-w-0 items-center gap-3 md:flex">
            <h2 className="text-sm font-semibold text-app-text">Leistungen</h2>
            <span className="shrink-0 text-xs text-app-text-subtle">
              {resultsCount.toLocaleString('de-DE')} Ergebnisse
            </span>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <div className="relative min-w-0 flex-1 md:max-w-[21rem] md:flex-none md:w-[21rem]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-app-text-subtle" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    triggerLeistungSearch();
                  }
                }}
                placeholder="Leistungen durchsuchen…"
                className="h-9 w-full rounded-md border border-app-border py-0 pl-8 pr-8 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
              />
              {searchSubmitting ? (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-app-text-subtle">
                  <LoadingSpinner size="sm" />
                </span>
              ) : searchInput ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover hover:text-app-text"
                  aria-label="Suche leeren"
                  title="Suche leeren"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                onError(null);
                setCreateOpen(true);
              }}
              className="hidden md:inline-flex h-9 shrink-0 items-center rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
            >
              + Leistung
            </button>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-app-border">
          {loading ? (
            <CardListSkeleton label="Leistungen werden geladen …" />
          ) : leistungen.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-app-text-subtle">{emptyMessage}</p>
          ) : (
            leistungen.map((row) => (
              <div
                key={row.id}
                className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-app-hover active:bg-app-surface-muted"
                onClick={() => { setEditDraft(leistungToEditDraft(row)); setMobileEditLeistung(row); }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-text truncate">{row.name}</p>
                  <p className="text-xs text-app-text-subtle truncate">
                    {[row.article_number, row.unit].filter(Boolean).join(' · ') || '-'}
                  </p>
                </div>
                <div className="shrink-0 text-left">
                  <p className="text-sm font-semibold text-app-text">{formatPrice(row.gross_price)} VK</p>
                  <p className="text-xs text-app-text-subtle">{formatPrice(row.net_cost)} Kosten</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div
          ref={setTableScrollNode}
          className="app-scrollbar overflow-x-auto md:min-h-0 md:flex-1 md:overflow-auto"
        >
          <table className="hidden md:table min-w-full text-sm border-collapse text-left">
            {/* Trennlinie als Innenschatten: bei border-collapse wandert ein `border-b` nicht mit der klebenden Zelle. */}
            <thead>
              <tr>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
                  Bezeichnung
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
                  Nr.
                </th>
                <th scope="col" className="sticky top-0 z-10 max-w-[220px] bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
                  Beschreibung
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
                  Einheit
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
                  Ordner
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
                  Kosten
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
                  Zuschlag %
                </th>
                <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
                  VK
                </th>
                <th
                  scope="col"
                  className="sticky top-0 z-10 w-24 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle"
                  aria-label="Aktionen"
                />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeletonRows columns={9} label="Leistungen werden geladen …" />
              ) : leistungen.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-4 text-center text-xs text-app-text-subtle">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                leistungen.map((row) => {
                  const isEditing = editingId === row.id;
                  const draft = isEditing ? { ...row, ...editDraft } : row;

                  return (
                    <tr key={row.id} className="border-b border-app-border hover:bg-app-hover">
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            value={draft.name}
                            onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                            className="w-full border border-app-border rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          row.name
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            value={draft.article_number ?? ''}
                            onChange={(e) =>
                              setEditDraft((p) => ({ ...p, article_number: e.target.value || null }))
                            }
                            className="w-full min-w-[110px] border border-app-border rounded px-2 py-1 text-xs"
                            placeholder="Optional"
                          />
                        ) : (
                          row.article_number || '-'
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[220px]">
                        {isEditing ? (
                          <textarea
                            value={draft.description ?? ''}
                            onChange={(e) =>
                              setEditDraft((p) => ({ ...p, description: e.target.value || null }))
                            }
                            rows={2}
                            className="w-full border border-app-border rounded px-2 py-1 text-xs resize-y min-w-[140px]"
                          />
                        ) : (
                          <ClampedTextWithTooltip
                            text={row.description}
                            className="text-xs text-app-text-muted"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <AppSelect
                            value={draft.unit || ''}
                            onChange={(v) => setEditDraft((p) => ({ ...p, unit: v || '' }))}
                            options={OFFER_UNIT_OPTIONS}
                            size="sm"
                          />
                        ) : (
                          row.unit || '-'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <AppSelect
                            value={draft.folder_id || ''}
                            onChange={(v) => setEditDraft((p) => ({ ...p, folder_id: v || null }))}
                            options={folderOptions}
                            size="sm"
                          />
                        ) : (
                          folderNameById.get(row.folder_id ?? '') || '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <input
                            value={draft.net_cost != null ? String(draft.net_cost) : ''}
                            onChange={(e) =>
                              setEditDraft((p) => ({ ...p, net_cost: parseGermanNumber(e.target.value) }))
                            }
                            className="w-24 border border-app-border rounded px-2 py-1 text-xs text-right"
                          />
                        ) : (
                          formatPrice(row.net_cost)
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <input
                            value={
                              draft.surcharge_percent != null && draft.surcharge_percent !== undefined
                                ? String(draft.surcharge_percent)
                                : ''
                            }
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              setEditDraft((p) => ({
                                ...p,
                                surcharge_percent: v === '' ? 0 : parseGermanNumber(v),
                              }));
                            }}
                            className="w-16 border border-app-border rounded px-2 py-1 text-xs text-right"
                          />
                        ) : typeof row.surcharge_percent === 'number' ? (
                          `${row.surcharge_percent} %`
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing
                          ? formatPrice(
                              calcVk(
                                draft.net_cost != null ? String(draft.net_cost) : '',
                                String(draft.surcharge_percent ?? 0)
                              )
                            )
                          : formatPrice(row.gross_price)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <div className="flex flex-col items-end gap-2">
                            <button
                              type="button"
                              className="text-xs text-pipedrive-blue hover:underline"
                              onClick={() => {
                                if (!draft.name?.trim()) {
                                  onError('Name ist erforderlich.');
                                  return;
                                }
                                if (!(draft.unit ?? '').trim()) {
                                  onError('Einheit ist erforderlich.');
                                  return;
                                }
                                const kostenVal = draft.net_cost ?? row.net_cost;
                                if (
                                  kostenVal === null ||
                                  kostenVal === undefined ||
                                  !Number.isFinite(Number(kostenVal))
                                ) {
                                  onError('Kosten sind erforderlich.');
                                  return;
                                }
                                const sur = Number(draft.surcharge_percent ?? 0);
                                if (!validateSurcharge(sur)) {
                                  onError('Der Zuschlag darf nicht negativ sein.');
                                  return;
                                }
                                setSaving(true);
                                onError(null);
                                updateLeistung(row.id, {
                                  article_number: draft.article_number ?? null,
                                  name: draft.name!,
                                  description: draft.description ?? row.description ?? null,
                                  unit: draft.unit ?? '',
                                  folder_id: draft.folder_id ?? null,
                                  net_cost: Number(kostenVal),
                                  surcharge_percent: sur,
                                })
                                  .then(() => {
                                    refreshAfterMutation();
                                    setEditingId(null);
                                    setEditDraft({});
                                  })
                                  .catch((err) => onError(err.message || 'Speichern fehlgeschlagen.'))
                                  .finally(() => setSaving(false));
                              }}
                            >
                              Speichern
                            </button>
                            <button
                              type="button"
                              className="text-xs text-app-text-subtle hover:underline"
                              onClick={() => {
                                setEditingId(null);
                                setEditDraft({});
                              }}
                            >
                              Abbrechen
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-app-hover"
                              onClick={() => {
                                setEditingId(row.id);
                                setEditDraft(leistungToEditDraft(row));
                              }}
                            >
                              <Pencil className="w-4 h-4 text-app-text-subtle" />
                            </button>
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-red-50"
                              onClick={() => {
                                setSaving(true);
                                deleteLeistung(row.id)
                                  .then(() => refreshAfterMutation())
                                  .catch((err) => onError(err.message || 'Löschen fehlgeschlagen.'))
                                  .finally(() => setSaving(false));
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {listTotalPages > 1 && (
          <MaterialSearchPagination
            searchPage={listPage}
            searchTotalPages={listTotalPages}
            loading={loading}
            onPageChange={(nextPage) => {
              setListPage(nextPage);
              listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        )}
      </section>

      {/* Mobile fullscreen edit overlay */}
      {mobileEditLeistung && (
        (() => {
          const row = mobileEditLeistung;
          const draft = { ...row, ...editDraft };
          const editVkPreview = calcVk(
            draft.net_cost != null ? String(draft.net_cost) : '',
            String(draft.surcharge_percent ?? 0)
          );
          const closeEdit = () => { setMobileEditLeistung(null); setEditDraft({}); };
          return (
            <div className="fixed inset-0 z-[60] flex flex-col bg-app-surface">
              <div className="shrink-0 px-4 py-3 border-b border-app-border flex items-center gap-3 bg-app-surface-muted/50">
                <button type="button" onClick={closeEdit} className="p-1.5 -ml-1 hover:bg-app-hover rounded" aria-label="Zurück">
                  <ArrowLeft className="w-5 h-5 text-app-text-muted" />
                </button>
                <h2 className="text-base font-semibold text-app-text flex-1 truncate">{row.name}</h2>
                <button
                  type="button"
                  disabled={saving}
                  className="btn-pipedrive-green text-sm py-1.5 px-3 disabled:opacity-50"
                  onClick={() => {
                    if (!draft.name?.trim()) { onError('Name ist erforderlich.'); return; }
                    if (!(draft.unit ?? '').trim()) { onError('Einheit ist erforderlich.'); return; }
                    const kostenVal = draft.net_cost ?? row.net_cost;
                    if (kostenVal === null || kostenVal === undefined || !Number.isFinite(Number(kostenVal))) { onError('Kosten sind erforderlich.'); return; }
                    const sur = Number(draft.surcharge_percent ?? 0);
                    if (!validateSurcharge(sur)) { onError('Zuschlag darf nicht negativ sein.'); return; }
                    setSaving(true); onError(null);
                    updateLeistung(row.id, {
                      article_number: draft.article_number ?? null,
                      name: draft.name!,
                      description: draft.description ?? row.description ?? null,
                      unit: draft.unit ?? '',
                      folder_id: draft.folder_id ?? null,
                      net_cost: Number(kostenVal),
                      surcharge_percent: sur,
                    })
                      .then(() => { refreshAfterMutation(); closeEdit(); })
                      .catch((err: Error) => onError(err.message || 'Speichern fehlgeschlagen.'))
                      .finally(() => setSaving(false));
                  }}
                >
                  Speichern
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-app-text-muted mb-1">Leistungsnummer</label>
                  <input
                    value={draft.article_number ?? ''}
                    onChange={(e) =>
                      setEditDraft((p) => ({ ...p, article_number: e.target.value || null }))
                    }
                    className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-app-text-muted mb-1">Name*</label>
                  <input value={draft.name} onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                    className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-app-text-muted mb-1">Einheit*</label>
                    <AppSelect value={draft.unit || ''} onChange={(v) => setEditDraft((p) => ({ ...p, unit: v || '' }))} options={OFFER_UNIT_OPTIONS} size="sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-app-text-muted mb-1">Ordner</label>
                    <AppSelect
                      value={draft.folder_id || ''}
                      onChange={(v) => setEditDraft((p) => ({ ...p, folder_id: v || null }))}
                      options={folderOptions}
                      size="sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-app-text-muted mb-1">Kosten*</label>
                    <input value={draft.net_cost != null ? String(draft.net_cost) : ''}
                      onChange={(e) => setEditDraft((p) => ({ ...p, net_cost: parseGermanNumber(e.target.value) }))}
                      inputMode="decimal"
                      className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-app-text-muted mb-1">Zuschlag (%)</label>
                    <input value={draft.surcharge_percent != null ? String(draft.surcharge_percent) : ''}
                      onChange={(e) => { const v = e.target.value.trim(); setEditDraft((p) => ({ ...p, surcharge_percent: v === '' ? 0 : parseGermanNumber(v) })); }}
                      inputMode="decimal"
                      className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none" />
                  </div>
                </div>
                <div className="rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text-muted">
                  <span className="font-medium text-app-text-muted">VK (berechnet):</span>{' '}
                  <span className="tabular-nums">{formatPrice(editVkPreview)}</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-app-text-muted mb-1">Beschreibung</label>
                  <textarea value={draft.description ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, description: e.target.value || null }))}
                    rows={3} className="w-full border border-app-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none resize-none" />
                </div>
                <div className="pt-2 border-t border-app-border">
                  <button
                    type="button"
                    className="w-full py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    onClick={() => {
                      if (!confirm('Leistung wirklich löschen?')) return;
                      setSaving(true);
                      deleteLeistung(row.id)
                        .then(() => { refreshAfterMutation(); closeEdit(); })
                        .catch((err: Error) => onError(err.message || 'Löschen fehlgeschlagen.'))
                        .finally(() => setSaving(false));
                    }}
                  >
                    Leistung löschen
                  </button>
                </div>
              </div>
            </div>
          );
        })()
      )}

      <LeistungCreateModal
        open={createOpen || mobileCreateOpen}
        onClose={closeCreate}
        saving={saving}
        previewVkLabel={formatPrice(previewVk)}
        error={createOpen || mobileCreateOpen ? error : null}
        name={lName}
        articleNumber={lArticleNumber}
        unit={lUnit}
        kosten={lKosten}
        surcharge={lSurcharge}
        description={lDescription}
        onNameChange={setLName}
        onArticleNumberChange={setLArticleNumber}
        onUnitChange={setLUnit}
        onKostenChange={setLKosten}
        onSurchargeChange={setLSurcharge}
        onDescriptionChange={setLDescription}
        onSubmit={(e) => {
          void handleCreate(e).catch(() => undefined);
        }}
      />
    </>
  );
};

